import React, { FC } from 'react';
import { CountryAlpha3Code, countriesByAlpha3Code } from '@cmctechnology/webinvest-constants';
import { DropdownMenu, IResponsiveDropdownMenuProps } from '../Dropdown/DropdownMenu';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { DropdownBaseItem, dropDownIconSize } from '../DropdownItem/DropdownBaseItem';
import { CountryFlag } from '../../components/CountryFlag/CountryFlag';
import { StyleIdentifier, useDropdown } from '../Dropdown/useDropdown';
import { AvatarSize } from '../../components/Avatar/Avatar';
import styled, { css } from 'styled-components';
import { ScrollingContainer } from '../../fundamentals/spaces/ScrollingContainer';
import { OFFGRID_SIZES, SIZES, sizes } from '../../fundamentals/sizes/sizes';
import { BaseDiv } from '../../common/BaseElements';
import { Icon } from '../../components/Icon/Icon';

export interface ICountryDropdownMenuProps extends IResponsiveDropdownMenuProps {
  primaryList?: CountryAlpha3Code[];
  secondaryList?: CountryAlpha3Code[];
  onClick: (code: string) => void;
  maxHeight?: string;
  linkPrefix?: string;
  selectedCountry?: CountryAlpha3Code;
  showSelectedCountry?: boolean;
}

export const defaultAlpha3List = [
  CountryAlpha3Code.AUS,
  CountryAlpha3Code.USA,
  CountryAlpha3Code.GBR,
  CountryAlpha3Code.BEL,
  CountryAlpha3Code.CAN,
  CountryAlpha3Code.DNK,
  CountryAlpha3Code.FRA,
  CountryAlpha3Code.DEU,
  CountryAlpha3Code.HKG,
  CountryAlpha3Code.JPN,
  CountryAlpha3Code.NLD,
  CountryAlpha3Code.NZL,
  CountryAlpha3Code.ESP,
  CountryAlpha3Code.SWE,
  CountryAlpha3Code.CHE
];

type MaxHeightProps = Pick<ICountryDropdownMenuProps, 'maxHeight'>;

const CountriesScrollContainer = styled(ScrollingContainer)<MaxHeightProps>`
  ${({ maxHeight }) =>
    maxHeight &&
    css`
      min-height: ${sizes[SIZES.XXX_LARGE]};
      max-height: ${maxHeight};
    `}
`;

const CountriesContainer = styled(BaseDiv)<MaxHeightProps>`
  ${({ maxHeight }) =>
    maxHeight &&
    css`
      padding-top: ${sizes[OFFGRID_SIZES.OFFGRID_6]};
      padding-bottom: ${sizes[OFFGRID_SIZES.OFFGRID_6]};
      background-color: transparent;
    `}
`;

export const getDefaultCountries = (defaultList: CountryAlpha3Code[], excludedList?: CountryAlpha3Code[]) =>
  defaultList.filter((defaultAlpha3Code) => !excludedList?.some((alpha3Code) => defaultAlpha3Code === alpha3Code));

export type ICountryDropdownMenuItemsProps = ICountryDropdownMenuProps & { styleIdentifier: StyleIdentifier };

const StyledAnchor = styled.a`
  text-decoration: none;
`;

const AlignEnd = styled(BaseDiv)`
  flex-grow: 1;
`;
const SelectedCheck: FC<{ selected: boolean }> = ({ selected }) => {
  return selected ? (
    <>
      <AlignEnd />
      <Icon name='check' />
    </>
  ) : (
    <></>
  );
};

export const CountryDropdownMenuItems: React.FC<ICountryDropdownMenuItemsProps> = ({
  primaryList,
  secondaryList,
  onClick,
  styleIdentifier,
  maxHeight,
  linkPrefix,
  selectedCountry,
  showSelectedCountry,
  ...rest
}) => {
  const { generateTestId } = useTestId(rest, `CountryDropdownMenuItems`);
  const hasPrimaryCountries = primaryList && primaryList.length > 0;
  const secondaryCountries = secondaryList ?? getDefaultCountries(defaultAlpha3List, primaryList);
  const topSecondaryCountry = countriesByAlpha3Code[secondaryCountries[0]];

  return (
    <CountriesContainer maxHeight={maxHeight}>
      {' '}
      <CountriesScrollContainer maxHeight={maxHeight}>
        {[...(primaryList ?? []), ...secondaryCountries].map((alpha3Code) => {
          const country = countriesByAlpha3Code[alpha3Code];

          const handleOnClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
            event.preventDefault();
            onClick(alpha3Code);
          };

          const showDivider = hasPrimaryCountries && country.name === topSecondaryCountry?.name ? `top` : false;

          return linkPrefix ? (
            <StyledAnchor key={country.name} href={`${linkPrefix}?country=${alpha3Code}`} role='menuitem'>
              <DropdownBaseItem onClick={handleOnClick} divider={showDivider} {...generateTestId(country.name)}>
                <CountryFlag code={alpha3Code} size={dropDownIconSize[styleIdentifier] as AvatarSize} />
                {country.name}
                <SelectedCheck selected={!!showSelectedCountry && country.alpha3 === selectedCountry} />
              </DropdownBaseItem>
            </StyledAnchor>
          ) : (
            <DropdownBaseItem key={country.name} onClick={handleOnClick} divider={showDivider} {...generateTestId(country.name)}>
              <CountryFlag code={alpha3Code} size={dropDownIconSize[styleIdentifier] as AvatarSize} />
              {country.name}
              <SelectedCheck selected={!!showSelectedCountry && country.alpha3 === selectedCountry} />
            </DropdownBaseItem>
          );
        })}
      </CountriesScrollContainer>
    </CountriesContainer>
  );
};

export const CountryDropdownMenu: React.FC<ICountryDropdownMenuProps> = ({ primaryList, secondaryList, onClick, maxHeight, ...rest }) => {
  const { generateTestId } = useTestId(rest, `CountryDropdownMenu`);
  const { styleIdentifier } = useDropdown(rest);

  return (
    <DropdownMenu {...rest}>
      <CountryDropdownMenuItems
        maxHeight={maxHeight}
        primaryList={primaryList}
        secondaryList={secondaryList}
        onClick={onClick}
        styleIdentifier={styleIdentifier}
        {...generateTestId()}
      />
    </DropdownMenu>
  );
};
